/**
 * /agency layout — the sports-agent persona's workspace.
 *
 * Gates child routes on administering at least one `type='agency'` org
 * (Member role owner|admin). The check runs in a server-side
 * `beforeLoad` so a deep link never flashes agency UI to someone who
 * can't use it.
 *
 * An agent who picked the persona but hasn't created their agency yet is
 * sent to /settings/organizations rather than bounced to /dashboard —
 * creating the agency IS their next step, and dumping them on the
 * athlete dashboard would be a dead end.
 *
 * Sidebar is a plain SSR <aside> with no motion wrapper, matching
 * compliance.tsx: framer-motion's FadeIn mounts at opacity 0 after
 * hydration and made the nav flicker on every layout remount.
 *
 * NOT gated on a paid plan — roster management is free by design
 * (founder ruling 2026-06-20). The recruiting dataset is the paid
 * unlock and gates itself when it lands.
 */
import { createFileRoute, Link, Outlet, redirect } from '@tanstack/react-router';

export const Route = createFileRoute('/_authenticated/agency')({
  beforeLoad: async ({ context }) => {
    const { getServerApi } = await import('../../lib/api-server');
    const serverApi = await getServerApi();
    const { agencies } = await context.queryClient.ensureQueryData(
      serverApi.agency.myAgencies.queryOptions(),
    );
    if (agencies.length === 0) {
      // No agency yet — send them to create one instead of a dead end.
      throw redirect({ to: '/settings/organizations' });
    }
  },
  component: AgencyLayout,
});

function AgencyLayout() {
  return (
    <div className="flex min-h-screen">
      <aside className="w-56 shrink-0 border-r bg-card p-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Agency
        </p>
        <nav className="flex flex-col gap-1 text-sm">
          <Link
            to="/agency/roster"
            className="rounded px-2 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            activeProps={{ className: 'bg-muted text-foreground font-medium' }}
          >
            Roster
          </Link>
          <Link
            to="/agency/recruit"
            className="rounded px-2 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            activeProps={{ className: 'bg-muted text-foreground font-medium' }}
          >
            Recruit
          </Link>
        </nav>
      </aside>
      <main className="flex-1 p-6">
        <Outlet />
      </main>
    </div>
  );
}
